import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/MonitoraggioDocumentiGiocatore.css";

const API_URL = process.env.REACT_APP_API_URL;

const STATI = [
  { value: "", label: "Tutti" },
  { value: "ATTIVO", label: "Attivo" },
  { value: "INATTIVO", label: "Inattivo" }
];

export default function MonitoraggioDocumentiGiocatore() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [categorie, setCategorie] = useState([]);
  const [giocatori, setGiocatori] = useState([]);
  const [documenti, setDocumenti] = useState([]);

  const [idCategoria, setIdCategoria] = useState("");
  const [statoGiocatore, setStatoGiocatore] = useState("");
  const [idGiocatore, setIdGiocatore] = useState("");

  const [loadingFiltri, setLoadingFiltri] = useState(false);
  const [loadingDocumenti, setLoadingDocumenti] = useState(false);

  const formatTipo = (tipo) => {
    switch (tipo) {
      case "CERTIFICATO_MEDICO":
        return "Certificato medico";
      case "STATO_FAMIGLIA":
        return "Stato di famiglia";
      case "DOCUMENTO_IDENTITA":
        return "Documento identità";
      case "ALTRO":
        return "Altro";
      default:
        return tipo;
    }
  };

  const fetchCategorie = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/lookup/categorie`);
      if (!res.ok) throw new Error("Errore nel recupero categorie");

      const data = await res.json().catch(() => []);
      setCategorie(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Errore caricamento categorie");
    }
  }, [authFetch]);

  const fetchGiocatori = useCallback(async () => {
    try {
      setLoadingFiltri(true);

      const params = new URLSearchParams();
      if (idCategoria) params.append("idCategoria", idCategoria);
      if (statoGiocatore) params.append("stato", statoGiocatore);

      const query = params.toString() ? `?${params.toString()}` : "";
      const res = await authFetch(`${API_URL}/api/admin/giocatori/lookup${query}`);

      if (!res.ok) throw new Error("Errore nel recupero giocatori");

      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : [];
      setGiocatori(lista);

      if (idGiocatore) {
        const esiste = lista.some((g) => String(g.id) === String(idGiocatore));
        if (!esiste) setIdGiocatore("");
      }
    } catch (err) {
      toast.error(err.message || "Errore caricamento giocatori");
    } finally {
      setLoadingFiltri(false);
    }
  }, [authFetch, idCategoria, statoGiocatore, idGiocatore]);

  const fetchDocumenti = useCallback(async () => {
    try {
      setLoadingDocumenti(true);

      const params = new URLSearchParams();
      if (idCategoria) params.append("idCategoria", idCategoria);
      if (statoGiocatore) params.append("statoGiocatore", statoGiocatore);
      if (idGiocatore) params.append("idGiocatore", idGiocatore);

      const query = params.toString() ? `?${params.toString()}` : "";
      const res = await authFetch(`${API_URL}/api/admin/documenti-giocatore${query}`);

      if (!res.ok) throw new Error("Errore nel recupero documenti");

      const data = await res.json().catch(() => []);
      setDocumenti(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Errore caricamento documenti");
    } finally {
      setLoadingDocumenti(false);
    }
  }, [authFetch, idCategoria, statoGiocatore, idGiocatore]);

  useEffect(() => {
    fetchCategorie();
  }, [fetchCategorie]);

  useEffect(() => {
    fetchGiocatori();
  }, [fetchGiocatori]);

  useEffect(() => {
    fetchDocumenti();
  }, [fetchDocumenti]);

  const handleReset = () => {
    setIdCategoria("");
    setStatoGiocatore("");
    setIdGiocatore("");
  };

  const handleDownload = async (idDocumento, nomeFile) => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/documenti-giocatore/${idDocumento}/download`);

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Errore durante il download");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeFile || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Errore durante il download");
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="sfondo-immagine">
      <div className="monitoraggio-documenti-page">
        <div className="monitoraggio-documenti-container">
          <h2 className="monitoraggio-documenti-title">Documenti Giocatori</h2>

          <div className="monitoraggio-documenti-filtri">
            <div className="monitoraggio-documenti-field">
              <label>Categoria</label>
              <select
                value={idCategoria}
                onChange={(e) => setIdCategoria(e.target.value)}
              >
                <option value="">Tutte</option>
                {categorie.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="monitoraggio-documenti-field">
              <label>Stato giocatore</label>
              <select
                value={statoGiocatore}
                onChange={(e) => setStatoGiocatore(e.target.value)}
              >
                {STATI.map((stato) => (
                  <option key={stato.value || "ALL"} value={stato.value}>
                    {stato.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="monitoraggio-documenti-field">
              <label>Giocatore</label>
              <select
                value={idGiocatore}
                onChange={(e) => setIdGiocatore(e.target.value)}
                disabled={loadingFiltri}
              >
                <option value="">
                  {loadingFiltri ? "Caricamento..." : "Tutti"}
                </option>
                {giocatori.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.cognome} {g.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="monitoraggio-documenti-actions">
            <button type="button" onClick={handleReset}>
              Reset filtri
            </button>

            <button
              type="button"
              className="secondary"
              onClick={() => navigate("/gestionale")}
            >
              Torna al gestionale
            </button>
          </div>
        </div>

        <div className="monitoraggio-documenti-container">
          <h3 className="monitoraggio-documenti-subtitle">Elenco documenti</h3>

          {loadingDocumenti ? (
            <p className="monitoraggio-documenti-empty">Caricamento documenti...</p>
          ) : documenti.length === 0 ? (
            <p className="monitoraggio-documenti-empty">Nessun documento trovato.</p>
          ) : (
            <div className="monitoraggio-documenti-list">
              {documenti.map((doc) => (
                <div className="monitoraggio-documenti-card" key={doc.id}>
                  <div className="monitoraggio-documenti-card-main">
                    <div className="monitoraggio-documenti-badge">
                      {formatTipo(doc.tipoDocumento)}
                    </div>

                    <h4>
                      {doc.cognomeGiocatore} {doc.nomeGiocatore}
                    </h4>

                    <p>Categoria: {doc.categoria || "-"}</p>
                    <p>Stato: {doc.statoGiocatore || "-"}</p>
                    <p>Documento: {doc.nomeFileOriginale}</p>
                    <p>Dimensione: {formatBytes(doc.dimensioneFile)}</p>
                    <p>
                      Caricato il:{" "}
                      {doc.dataCaricamento
                        ? new Date(doc.dataCaricamento).toLocaleString("it-IT")
                        : "-"}
                    </p>
                    {doc.note && <p>Note: {doc.note}</p>}
                  </div>

                  <div className="monitoraggio-documenti-card-actions">
                    <button
                      type="button"
                      onClick={() => handleDownload(doc.id, doc.nomeFileOriginale)}
                    >
                      Scarica PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}